'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import Raven from 'raven-js';
import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import { Networks, EventTypes, OfflineEventTriggers, isQA } from 'AdsUI/lib/Constants';
import H2 from 'UIComponents/elements/headings/H2';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModalPanel from 'UIComponents/dialog/UIModalPanel';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import OfflineEventCreationButton from 'AdsUI/components/events/OfflineEventCreationButton';
import { getAccountsByNetwork, getConnectedNetworks } from 'AdsUI/selectors/AccountsSelectors';
import { fetchPixels } from 'AdsUI/actions/AudiencesActions';
import { postTimedNotification } from 'AdsUI/actions/NotificationActions';
import { openEventPanelType, closeEventPanel, fetchEventList } from 'AdsUI/actions/EventSettingsActions';
import { usageTracker } from 'AdsUI/lib/usageTracker';
import GenericErrorBoundary from 'AdsUI/components/GenericErrorBoundary';
import ErrorPanelBody from 'AdsUI/components/ErrorPanelBody';
import makeLoadable from 'AdsUI/lib/makeLoadable';
import { getEventsRequestStatus, getEnabledEventsCountForLimit } from 'AdsUI/selectors/EventsSelectors';
import { getEventLimits, getFacebookCapiLCSEvents } from 'AdsUI/selectors/AuthSelectors';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { getAccountHasBusinessManager } from 'AdsUI/data/FBData';
import FeedbackLoader from 'feedback-loader';
import { fetchScopes, saveFBEUserToken } from 'AdsUI/data/AccountConnectionData';
import { getPixelAdAccounts } from 'AdsUI/selectors/PixelInstallationSelectors';
import { getPixels } from 'AdsUI/selectors/AudiencesSelectors';
import FacebookDataSyncModal from 'ads-account-connection/components/FacebookDataSyncModal';
import OAuthHandler from 'ads-account-connection/components/OAuthHandler';
import FacebookCapiEventCreationButton from 'AdsUI/components/events/FacebookCapiEventCreationButton';
import GoogleECLCSEventCreationButton from 'AdsUI/components/events/GoogleECLCSEventCreationButton';
import EventCreationButtonContainer from './EventCreationButtonContainer';
import { FACEBOOK_CAPI_LIFECYCLE_STAGES } from 'AdsUI/components/events/FacebookCapiOptionsPanel';
import { getEventAdAccount } from 'AdsUI/lib/events';
const EVENT_NOT_CREATED_SURVEY_ID = isQA ? 245 : 785;
const ADWORDS_FORM_SUBMISSION_SURVEY_ID = isQA ? 244 : 784;
const FACEBOOK_ADWORDS_LIFECYCLE_SURVEY_ID = isQA ? 276 : 844;
const LINKEDIN_LIFECYCLE_SURVEY_ID = isQA ? 275 : 843;
const FACEBOOK_FORM_SUBMISSION_SURVEY_ID = isQA ? 274 : 842;

const loadSurvey = (eventCreated, eventTrigger, selectedNetwork) => {
  if (!window.hsFeedback || !window.hsFeedback.loadSurvey) {
    window.setTimeout(loadSurvey, 1000);
    return;
  }

  if (!eventCreated) {
    window.hsFeedback.loadSurvey('CSAT', EVENT_NOT_CREATED_SURVEY_ID);
    return;
  }

  if (eventTrigger === OfflineEventTriggers.LIFECYCLE_STAGE_CHANGE && selectedNetwork === Networks.LINKEDIN) {
    window.hsFeedback.loadSurvey('CSAT', LINKEDIN_LIFECYCLE_SURVEY_ID);
    return;
  }

  if (eventTrigger === OfflineEventTriggers.LIFECYCLE_STAGE_CHANGE) {
    window.hsFeedback.loadSurvey('CSAT', FACEBOOK_ADWORDS_LIFECYCLE_SURVEY_ID);
    return;
  }

  if (eventTrigger === OfflineEventTriggers.FORM_SUBMISSION && selectedNetwork === Networks.FACEBOOK) {
    window.hsFeedback.loadSurvey('CSAT', FACEBOOK_FORM_SUBMISSION_SURVEY_ID);
    return;
  }

  if (eventTrigger === OfflineEventTriggers.FORM_SUBMISSION && selectedNetwork === Networks.ADWORDS) {
    window.hsFeedback.loadSurvey('CSAT', ADWORDS_FORM_SUBMISSION_SURVEY_ID);
    return;
  }
};

const OfflineEventCreationPanelLoader = makeLoadable(() => import('AdsUI/components/events/OfflineEventCreationPanel'));
export const defaultFormSubmissionOptions = {
  eventName: 'HubSpot-',
  currency: 'USD'
};
/**
 * Find an installed pixel for the given ad account.
 * @param adAccount The ad account
 * @param availablePixels Pixels available to the user
 * @returns The first installed pixel in the ad account
 */

const findInstalledPixel = (adAccount, availablePixels) => availablePixels.find(pixel => pixel.accountId === adAccount.accountId && pixel.adNetwork === adAccount.network && pixel.installed === true);
/**
 * Get the default options/values for the event.
 * @param eventType The type of event
 * @param eventToReplace Optional event the user wants to replace
 * @param eventAdAccount The ad account that `eventToReplace` belongs to
 * @param defaultAccountPixel Default pixel to select, belonging to `eventAdAccount`
 * @returns Default event options
 */


const getDefaultEventOptions = (eventType, eventToReplace, eventAdAccount, defaultAccountPixel) => {
  let eventToReplaceData = null;

  if (eventToReplace && eventAdAccount) {
    const selectedLifecycleStages = FACEBOOK_CAPI_LIFECYCLE_STAGES.includes(eventToReplace.stageId) ? [eventToReplace.stageId] : [];
    eventToReplaceData = {
      currentEventTrigger: OfflineEventTriggers.LIFECYCLE_STAGE_CHANGE,
      facebookCapiLSC: {
        selectedLifecycleStages,
        numSelectedLifecycleStages: selectedLifecycleStages.length
      },
      selectedAdAccount: {
        adAccount: eventAdAccount,
        text: eventAdAccount.accountName,
        value: `${eventAdAccount.network}-${eventAdAccount.accountId}`
      },
      selectedPixel: defaultAccountPixel
    };
  }

  const eventTypeToDefaultOptions = {
    [EventTypes.CRM_EVENT]: {
      currentEventTrigger: Object.keys(OfflineEventTriggers).length === 1 ? Object.keys(OfflineEventTriggers)[0] : null,
      selectedLifecycleStageId: null,
      selectedEventCategoryId: null,
      stageValue: null,
      name: I18n.text('events.creation.CRM_EVENT.defaultName', {
        lifecycleStage: ''
      }),
      currency: 'USD',
      selectedAdAccounts: ImmutableSet(),
      selectedAdAccount: null,
      selectedPixel: null,
      usedStageIds: ImmutableMap(),
      isEstimatedValue: true,
      includeInGoogleConversions: false,
      consentInfo: null,
      pageViews: {},
      formSubmission: {
        eventName: 'HubSpot-',
        currency: 'USD'
      },
      facebookBusinessExtensionAuth: {},
      allowEventStreamingForContactsWithoutAdInteraction: true,
      facebookCapiLSC: {
        selectedLifecycleStages: FACEBOOK_CAPI_LIFECYCLE_STAGES,
        numSelectedLifecycleStages: FACEBOOK_CAPI_LIFECYCLE_STAGES.length
      }
    }
  };
  const defaultOptions = eventTypeToDefaultOptions[eventType];
  return eventToReplaceData ? Object.assign({}, defaultOptions, {}, eventToReplaceData) : defaultOptions;
};
/**
 * Get the default display value for the Facebook auth modal.
 * @param eventToReplace Optional event the user wants to replace
 * @param defaultAccountPixel Default pixel to select, belonging to `eventAdAccount`
 * @param hasFacebookCapiLCSEvents Determines if the user is un-gated for FB CAPI LCS events
 * @returns True or false
 */


const getDefaultOpenFBAuthModalValue = (eventToReplace, defaultAccountPixel, hasFacebookCapiLCSEvents) => {
  if (!eventToReplace || !defaultAccountPixel) {
    return false;
  }

  if (eventToReplace.adNetwork === Networks.FACEBOOK && eventToReplace.eventTriggerType === OfflineEventTriggers.LIFECYCLE_STAGE_CHANGE && hasFacebookCapiLCSEvents && defaultAccountPixel.fbeinstalled === false) {
    return true;
  }

  return false;
};

class EventCreationPanelContainer extends PureComponent {
  constructor(props) {
    super(props);
    let defaultAdAccount = null;
    let defaultPixel = null;

    if (props.eventToReplace) {
      defaultAdAccount = getEventAdAccount(props.eventToReplace, props.availableAdAccounts);
      defaultPixel = findInstalledPixel(defaultAdAccount, props.pixels);
    }

    this.state = {
      eventOptions: getDefaultEventOptions(EventTypes.CRM_EVENT, props.eventToReplace, defaultAdAccount, defaultPixel),
      showErrors: false,
      accountsWithoutBusinessManager: ImmutableSet(),
      hasIncorrectBusinessManagerPermissions: false,
      hasLinkedInScope: false,
      openFBAuthModal: getDefaultOpenFBAuthModalValue(props.eventToReplace, defaultPixel, props.hasFacebookCapiLCSEvents)
    };
    this.onEventOptionsChange = this.onEventOptionsChange.bind(this);
    this.setShowErrors = this.setShowErrors.bind(this);
    this.onCloseEventPanel = this.onCloseEventPanel.bind(this);
    this.setHasIncorrectBusinessManagerPermissions = this.setHasIncorrectBusinessManagerPermissions.bind(this);
    this.checkScope = this.checkScope.bind(this);
    this.oAuthSuccess = this.oAuthSuccess.bind(this);
    this.onOAuthFailure = this.onOAuthFailure.bind(this);
    this.handleFBAuthFailed = this.handleFBAuthFailed.bind(this);
  }

  componentDidMount() {
    const {
      eventRequestStatus,
      pixelRequestStatus
    } = this.props;

    if (!eventRequestStatus.fetched && !eventRequestStatus.fetching) {
      this.props.fetchEvents();
    }

    if (!pixelRequestStatus.fetched && !pixelRequestStatus.fetching) {
      this.props.fetchPixels();
    }

    usageTracker.track('modifyEvent', {
      action: 'Opened create event panel'
    });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.pixels !== this.props.pixels && !this.state.eventOptions.selectedPixel) {
      const {
        selectedAdAccount
      } = this.state.eventOptions;

      if (selectedAdAccount) {
        const {
          accountId,
          network
        } = selectedAdAccount.adAccount;
        const selectedPixel = this.props.pixels.find(pixel => {
          return pixel.accountId === accountId && pixel.adNetwork === network && pixel.installed === true;
        });

        if (selectedPixel) {
          this.setState(oldState => ({
            eventOptions: Object.assign({}, oldState.eventOptions, {
              selectedPixel
            })
          }));
        }
      }
    }
  }

  checkScope(scope) {
    fetchScopes(Networks.LINKEDIN).then(response => {
      this.setState({
        hasLinkedInScope: response.includes(scope)
      });
    }).catch(error => {
      Raven.captureMessage('Fetch scopes for linkedin failed', {
        extra: {
          error
        }
      });
    });
  }

  onEventOptionsChange(updatedOptions) {
    /*
    if selectedAdAccount changes
      if null, clear update eventOpts ie. selectedAdAccount
      if facebook, get BM
      if pixel && formSub OR LSC trigger *
        update selected pixel
        if FB and no auth, pop up auth
     if trigger changes to form sub OR LSC and account is selected
      update selected pixel
      if FB and no auth, pop up auth
     if form update, update eventOpt form state
     else update eventOpts
    */
    const {
      eventOptions
    } = this.state;

    const isFacebookCapiLCSEvent = eventTrigger => {
      return eventTrigger === OfflineEventTriggers.LIFECYCLE_STAGE_CHANGE && this.props.hasFacebookCapiLCSEvents;
    };

    if (updatedOptions.selectedAdAccount !== undefined) {
      const {
        selectedAdAccount
      } = updatedOptions;

      if (selectedAdAccount === null) {
        this.setState(oldState => ({
          eventOptions: Object.assign({}, oldState.eventOptions, {}, updatedOptions)
        }));
        return;
      }

      const {
        network,
        accountId
      } = updatedOptions.selectedAdAccount.adAccount;

      if (network === Networks.FACEBOOK) {
        getAccountHasBusinessManager(accountId).then(hasBusinessManager => {
          if (!hasBusinessManager) {
            this.setState(({
              accountsWithoutBusinessManager
            }) => ({
              accountsWithoutBusinessManager: accountsWithoutBusinessManager.add(updatedOptions.selectedAdAccount.adAccount)
            }));
          } else {
            this.setState(({
              accountsWithoutBusinessManager
            }) => ({
              accountsWithoutBusinessManager: accountsWithoutBusinessManager.delete(updatedOptions.selectedAdAccount.adAccount)
            }));
          }
        }).catch(error => {
          Raven.captureMessage('Fetch get account business manager failed', {
            extra: {
              error
            }
          });
        });
      }

      const newEventOptions = Object.assign({}, eventOptions, {}, updatedOptions); // update selected pixel

      const selectedPixel = this.props.pixels.find(pixel => {
        return pixel.accountId === accountId && pixel.adNetwork === network && pixel.installed === true;
      });

      if (selectedPixel) {
        newEventOptions.selectedPixel = selectedPixel;
        const currentEventTrigger = updatedOptions.currentEventTrigger !== undefined ? updatedOptions.currentEventTrigger : eventOptions.currentEventTrigger; // check FB auth for selected pixel

        if ((currentEventTrigger === OfflineEventTriggers.FORM_SUBMISSION || isFacebookCapiLCSEvent(currentEventTrigger)) && network === Networks.FACEBOOK && selectedPixel.fbeinstalled === false) {
          this.setState({
            openFBAuthModal: true
          });
        }
      } else {
        newEventOptions.selectedPixel = null;
      }

      if (eventOptions.currentEventTrigger === OfflineEventTriggers.FORM_SUBMISSION) {
        // reset selected form
        newEventOptions.formSubmission.selectedForm = null;
      }

      this.setState({
        eventOptions: newEventOptions
      });
      return;
    } // Trigger FB auth modal when user selects unauthorized FB pixel (updatedOptions.selectedPixel)
    // or default pixel is unauthorized (eventOptions.selectedPixel)


    const pixel = updatedOptions.selectedPixel || eventOptions.selectedPixel;

    if ((updatedOptions.currentEventTrigger === OfflineEventTriggers.FORM_SUBMISSION || isFacebookCapiLCSEvent(updatedOptions.currentEventTrigger)) && pixel && pixel.adNetwork === Networks.FACEBOOK && pixel.fbeinstalled === false) {
      this.setState(oldState => ({
        eventOptions: Object.assign({}, oldState.eventOptions, {}, updatedOptions),
        openFBAuthModal: true
      }));
      return;
    }

    if (updatedOptions.formSubmission) {
      this.setState(oldState => ({
        eventOptions: Object.assign({}, oldState.eventOptions, {
          formSubmission: Object.assign({}, oldState.eventOptions.formSubmission, {}, updatedOptions.formSubmission)
        })
      }));
      return;
    }

    this.setState(oldState => ({
      eventOptions: Object.assign({}, oldState.eventOptions, {}, updatedOptions)
    }));
  }

  setShowErrors(showErrors) {
    this.setState({
      showErrors
    });
  }

  setHasIncorrectBusinessManagerPermissions(hasIncorrectBusinessManagerPermissions) {
    this.setState({
      hasIncorrectBusinessManagerPermissions
    });
  }

  onCloseEventPanel(eventCreated) {
    this.props.closeEventPanel();
    const {
      currentEventTrigger,
      selectedAdAccount
    } = this.state.eventOptions;
    loadSurvey(eventCreated, currentEventTrigger, selectedAdAccount && selectedAdAccount.adAccount && selectedAdAccount.adAccount.network);
  }

  handleFBAuthFailed() {
    this.props.postTimedNotification({
      id: 'facebook-auth-failure',
      type: 'danger',
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ADS_ACCOUNT_CONNECTION.conversionsApi.installFacebookPixelModal.syncData.GENERIC_ERROR_MESSAGE"
      })
    });
  }

  oAuthSuccess(data) {
    const {
      facebookBusinessExtensionAuth,
      selectedPixel
    } = this.state.eventOptions;
    this.onEventOptionsChange({
      facebookBusinessExtensionAuth: Object.assign({}, facebookBusinessExtensionAuth, {
        userToken: data.accessToken,
        adAccountId: selectedPixel.accountId
      })
    });
    saveFBEUserToken(selectedPixel.accountId, facebookBusinessExtensionAuth.externalBusinessId, data.accessToken).then(() => {
      // Refetch pixels to pull in updated data for the newly authorized pixel
      this.props.fetchPixels().catch(error => {
        Raven.captureMessage('Failed to fetch pixels after saving FBE user token', {
          extra: {
            error
          }
        });
      });
    }).catch(error => {
      Raven.captureMessage('Failed to save FBE user token', {
        extra: {
          error
        }
      });
    });
  }

  onOAuthFailure() {
    this.handleFBAuthFailed();
  }

  render() {
    const {
      eventOptions,
      showErrors,
      accountsWithoutBusinessManager,
      hasIncorrectBusinessManagerPermissions,
      hasLinkedInScope,
      openFBAuthModal
    } = this.state;
    const {
      eventRequestStatus,
      enabledEventsCount,
      eventLimit,
      pixelAdAccounts
    } = this.props;
    const {
      selectedPixel,
      facebookBusinessExtensionAuth,
      currentEventTrigger,
      selectedAdAccount,
      facebookCapiLSC
    } = eventOptions;
    const network = selectedAdAccount && selectedAdAccount.adAccount ? selectedAdAccount.adAccount.network : null;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(UIModalPanel, {
        "data-test-id": "create-event-modal",
        width: 550,
        children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
          children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
            onClick: () => this.onCloseEventPanel(false)
          }), /*#__PURE__*/_jsx(H2, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "events.creation.header"
            })
          })]
        }), /*#__PURE__*/_jsxs(GenericErrorBoundary, {
          fallback: /*#__PURE__*/_jsx(ErrorPanelBody, {}),
          children: [/*#__PURE__*/_jsx(UIDialogBody, {
            children: eventRequestStatus.fetching ? /*#__PURE__*/_jsx(UILoadingSpinner, {
              layout: "centered"
            }) : /*#__PURE__*/_jsx(OfflineEventCreationPanelLoader, {
              eventOptions: eventOptions,
              onEventOptionsChange: this.onEventOptionsChange,
              enabledEventCount: enabledEventsCount,
              eventLimit: eventLimit,
              showErrors: showErrors,
              accountsWithoutBusinessManager: accountsWithoutBusinessManager,
              hasIncorrectBusinessManagerPermissions: hasIncorrectBusinessManagerPermissions,
              hasLinkedInScope: hasLinkedInScope,
              checkScope: this.checkScope,
              adAccounts: pixelAdAccounts,
              defaultNetwork: this.props.eventToReplace && this.props.eventToReplace.adNetwork,
              eventToReplace: this.props.eventToReplace
            })
          }), /*#__PURE__*/_jsx(FeedbackLoader, {
            clientLoadOnly: false
          })]
        }), /*#__PURE__*/_jsxs(UIDialogFooter, {
          children: [/*#__PURE__*/_jsx(EventCreationButtonContainer, {
            currentEventTrigger: currentEventTrigger,
            facebookCapiButton: /*#__PURE__*/_jsx(FacebookCapiEventCreationButton, {
              locked: facebookCapiLSC && facebookCapiLSC.numSelectedLifecycleStages + enabledEventsCount > eventLimit,
              eventOptions: eventOptions,
              toggleIsOpen: () => this.onCloseEventPanel(true),
              setShowErrors: this.setShowErrors,
              accountsWithoutBusinessManager: accountsWithoutBusinessManager,
              setHasIncorrectBusinessManagerPermissions: this.setHasIncorrectBusinessManagerPermissions,
              eventToReplace: this.props.eventToReplace
            }),
            googleECLCSButton: /*#__PURE__*/_jsx(GoogleECLCSEventCreationButton, {
              accountsWithoutBusinessManager: accountsWithoutBusinessManager,
              eventOptions: eventOptions,
              locked: enabledEventsCount >= eventLimit,
              setHasIncorrectBusinessManagerPermissions: this.setHasIncorrectBusinessManagerPermissions,
              setShowErrors: this.setShowErrors,
              toggleIsOpen: () => this.onCloseEventPanel(true)
            }),
            network: network,
            offlineEventButton: /*#__PURE__*/_jsx(OfflineEventCreationButton, {
              locked: enabledEventsCount >= eventLimit,
              eventOptions: eventOptions,
              toggleIsOpen: () => this.onCloseEventPanel(true),
              setShowErrors: this.setShowErrors,
              accountsWithoutBusinessManager: accountsWithoutBusinessManager,
              setHasIncorrectBusinessManagerPermissions: this.setHasIncorrectBusinessManagerPermissions,
              hasLinkedInScope: hasLinkedInScope
            })
          }), /*#__PURE__*/_jsx(UIButton, {
            onClick: () => this.onCloseEventPanel(false),
            use: "secondary",
            className: "m-left-3",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "cancelButton"
            })
          })]
        })]
      }), openFBAuthModal && selectedPixel && /*#__PURE__*/_jsx(FacebookDataSyncModal, {
        adAccountId: selectedPixel.accountId,
        pixelId: selectedPixel.id,
        onClose: () => this.setState({
          openFBAuthModal: false
        }),
        onExternalBusinessIdChange: id => {
          this.onEventOptionsChange({
            facebookBusinessExtensionAuth: Object.assign({}, facebookBusinessExtensionAuth, {
              externalBusinessId: id
            })
          });
        },
        setError: () => this.setState({
          showErrors
        }),
        isEventCreation: true
      }), facebookBusinessExtensionAuth.externalBusinessId && /*#__PURE__*/_jsx(OAuthHandler, {
        onSuccess: this.oAuthSuccess,
        onFailure: this.onOAuthFailure
      })]
    });
  }

}

export default connect(state => ({
  availableAdAccounts: getAccountsByNetwork(state),
  connectedNetworks: getConnectedNetworks(state),
  enabledEventsCount: getEnabledEventsCountForLimit(state),
  eventLimit: getEventLimits(state),
  eventRequestStatus: getEventsRequestStatus(state),
  pixelRequestStatus: state.requestStatus.pixels,
  pixelAdAccounts: getPixelAdAccounts(state),
  pixels: getPixels(state),
  hasFacebookCapiLCSEvents: getFacebookCapiLCSEvents(state),
  eventToReplace: state.events.panelState.eventToReplace
}), {
  fetchPixels,
  fetchEvents: fetchEventList,
  openEventPanelType,
  closeEventPanel,
  postTimedNotification
})(EventCreationPanelContainer);